/**
 * The icons of Harvey's look, drawn exactly as the approved prototype draws
 * them: 24 unit strokes at 1.5 in the text's own colour (svg.i in base.css), so
 * each follows the theme with no colour of its own. Decorative unless a label
 * is given: a control with only an icon names itself with aria-label.
 */

import type { JSX } from "preact";

const PATHS = {
  dashboard: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="13" y="4" width="7" height="4" rx="1.5" />
      <rect x="13" y="10" width="7" height="10" rx="1.5" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" />
    </>
  ),
  case: (
    <>
      <rect x="3.5" y="7" width="17" height="12" rx="1.5" />
      <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M3.5 12h17" />
    </>
  ),
  building: <path d="M5 20V5.5A1.5 1.5 0 0 1 6.5 4h7A1.5 1.5 0 0 1 15 5.5V20M15 10h3.5A1.5 1.5 0 0 1 20 11.5V20M3 20h18M8.5 8h3M8.5 12h3M8.5 16h3" />,
  book: <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5zM20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5a1.5 1.5 0 0 0 1.5-1.5z" />,
  cap: (
    <>
      <path d="M2.5 9.5L12 5l9.5 4.5L12 14z" />
      <path d="M6.5 11.5V16c3 2.5 8 2.5 11 0v-4.5" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" />
    </>
  ),
  panel: (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path d="M9 4.5v15" />
    </>
  ),
  upload: <path d="M12 16V5M7.5 9.5L12 5l4.5 4.5M5 19h14" />,
  download: <path d="M12 5v11M7.5 11.5L12 16l4.5-4.5M5 19h14" />,
  file: (
    <>
      <path d="M14 4H7a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8z" />
      <path d="M14 4v4h4" />
    </>
  ),
  cv: (
    <>
      <rect x="5" y="3.5" width="14" height="17" rx="1.5" />
      <circle cx="12" cy="9" r="2.2" />
      <path d="M8.5 15c.8-1.6 2-2.3 3.5-2.3s2.7.7 3.5 2.3M8.5 18h7" />
    </>
  ),
  mail: (
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" />
      <path d="M4 7l8 6 8-6" />
    </>
  ),
  layers: (
    <>
      <path d="M12 4l8 4-8 4-8-4z" />
      <path d="M4 12l8 4 8-4M4 16l8 4 8-4" />
    </>
  ),
  sign: (
    <>
      <path d="M4 17c3-6 5-9 7-9s-1 9 2 9 2-5 4-5 2 3 3 3" />
      <path d="M4 20h16" />
    </>
  ),
  pen: (
    <>
      <path d="M15 5l4 4L9 19H5v-4z" />
      <path d="M13 7l4 4" />
    </>
  ),
  quote: <path d="M9 7H6a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h3v1c0 1.5-1 2.5-2.5 3M19 7h-3a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h3v1c0 1.5-1 2.5-2.5 3" />,
  chat: <path d="M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-8l-4 3.5V16H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />,
  person: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c1-4 4-6 7-6s6 2 7 6" />
    </>
  ),
  left: <path d="M15 6l-6 6 6 6" />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M20 20l-4.5-4.5" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  card: (
    <>
      <rect x="3.5" y="6" width="17" height="12" rx="2" />
      <path d="M3.5 10h17M7 14.5h4" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6L6 18" />,
  out: <path d="M14 5h5v5M19 5l-8 8M17 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h4" />,
  calendar: (
    <>
      <rect x="4" y="5.5" width="16" height="14" rx="1.5" />
      <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
    </>
  ),
  /** A menu that opens: the account at the foot of the sidebar. */
  updown: <path d="M8 9.5l4-4 4 4M8 14.5l4 4 4-4" />,
  /** Filters: three rules, each shorter, the way a list narrows. */
  filter: <path d="M4 7h16M7 12h10M10 17h4" />,
  /** Log out: a door, and the way through it. */
  leave: <path d="M10 5H6a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h4M14 8l4 4-4 4M18 12H9" />,
  /** History: a clock, the time things were done. */
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  /** Information pill / tooltip trigger. */
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4M12 8h.01" />
    </>
  ),
} satisfies Record<string, JSX.Element>;

export type IconName = keyof typeof PATHS;

export const ICON_NAMES = Object.keys(PATHS) as IconName[];

export function Icon(props: { name: IconName; label?: string }) {
  return (
    <svg
      class="i"
      viewBox="0 0 24 24"
      aria-hidden={props.label ? undefined : "true"}
      role={props.label ? "img" : undefined}
      aria-label={props.label}
      focusable="false"
    >
      {PATHS[props.name]}
    </svg>
  );
}
